import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  MenuItem,
  InputAdornment,
  Chip,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/common/PageHeader';
import DataTable, { Column } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import { licensesApi, Subscription, Product } from '@/api/licenses-api';
import { usersApi, Customer } from '@/api/users-api';

export default function SubscriptionsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    status: '',
    customerId: '',
    productId: '',
    search: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  // Parse URL parameters for initial filter state
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filterParam = params.get('filter');
    
    if (filterParam === 'expiring') {
      setFilters(prev => ({ ...prev, status: 'expiring' }));
      setShowFilters(true);
    }
  }, [location]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch all subscriptions
      const subscriptionsData = await licensesApi.getSubscriptions();
      setSubscriptions(subscriptionsData);
      
      // Fetch products for filtering
      const productsData = await licensesApi.getProducts();
      setProducts(productsData);
      
      // Fetch customers for filtering
      if (user?.role !== 'customer') {
        const customersData = await usersApi.getCustomers();
        setCustomers(customersData);
      }
    } catch (err) {
      console.error('Error fetching subscription data:', err);
      setError('Failed to load subscriptions. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user?.role]);

  const handleCreateSubscription = () => {
    navigate('/subscriptions/new');
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters({ ...filters, [field]: value });
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      customerId: '',
      productId: '',
      search: '',
    });
  };

  // Apply filters to the subscription list
  const filteredSubscriptions = subscriptions.filter(subscription => {
    // Filter by status
    if (filters.status && filters.status !== 'expiring') {
      if (subscription.status !== filters.status) return false;
    } else if (filters.status === 'expiring') {
      // Check if subscription is expiring within 30 days
      const now = new Date();
      const expirationDate = new Date(subscription.endDate);
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(now.getDate() + 30);
      
      if (!(expirationDate <= thirtyDaysFromNow && expirationDate >= now)) return false;
    }
    
    // Filter by customer
    if (filters.customerId && subscription.customerId !== filters.customerId) return false;
    
    // Filter by product
    if (filters.productId && subscription.productId !== filters.productId) return false;
    
    // Filter by search term (subscription ID)
    if (filters.search && !subscription.chargebeeSubscriptionId.toLowerCase().includes(filters.search.toLowerCase())) return false;
    
    return true;
  });

  const columns: Column<Subscription>[] = [
    {
      id: 'chargebeeSubscriptionId',
      label: 'Subscription ID',
      minWidth: 150,
      format: (value) => value.substring(0, 10) + '...',
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 120,
      format: (value) => <StatusChip status={value} type="subscription" />,
    }
  ];

  // Add customer column for distributor/reseller view
  if (user?.role !== 'customer') {
    columns.push({
      id: 'customerId',
      label: 'Customer',
      minWidth: 150,
      format: (value) => {
        const customer = customers.find(c => c.customerId === value);
        return customer ? customer.name : 'Unknown';
      },
    });
  }

  // Add additional columns
  columns.push(
    {
      id: 'productId',
      label: 'Product',
      minWidth: 150,
      format: (value) => {
        const product = products.find(p => p.productId === value);
        return product ? product.description : 'Unknown';
      },
    },
    {
      id: 'startDate',
      label: 'Start Date',
      minWidth: 120,
      format: (value) => new Date(value).toLocaleDateString(),
    },
    {
      id: 'endDate',
      label: 'End Date',
      minWidth: 120,
      format: (value) => new Date(value).toLocaleDateString(),
    }
  );

  return (
    <>
      <PageHeader
        title="Subscriptions"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Subscriptions' },
        ]}
        action={
          user?.role !== 'customer' ? {
            label: 'New Subscription',
            onClick: handleCreateSubscription,
            icon: <AddIcon />,
          } : undefined
        }
      />

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Filters
              <IconButton size="small" onClick={() => setShowFilters(!showFilters)} sx={{ ml: 1 }}>
                <FilterIcon />
              </IconButton>
            </Typography>
            
            <Box>
              <Button 
                size="small" 
                startIcon={<ClearIcon />} 
                onClick={clearFilters}
                disabled={!Object.values(filters).some(Boolean)}
              >
                Clear Filters
              </Button>
            </Box>
          </Box>
          
          {showFilters && (
            <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3}}>
             <TextField
               fullWidth
               label="Search by Subscription ID"
               value={filters.search}
               onChange={(e) => handleFilterChange('search', e.target.value)}
               InputProps={{
                 startAdornment: (
                   <InputAdornment position="start">
                     <SearchIcon />
                   </InputAdornment>
                 ),
               }}
             />
           </Grid>
  
              
           <Grid size={{ xs: 12, sm: 6, md: 3}}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="expiring">Expiring Soon</MenuItem>
                </TextField>
              </Grid>
              
              {user?.role !== 'customer' && (
                <Grid size={{ xs: 12, md: 3}}>
                  <TextField
                    select
                    fullWidth
                    label="Customer"
                    value={filters.customerId}
                    onChange={(e) => handleFilterChange('customerId', e.target.value)}
                  >
                    <MenuItem value="">All Customers</MenuItem>
                    {customers.map((customer) => (
                      <MenuItem key={customer.customerId} value={customer.customerId}>
                        {customer.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              
              <Grid size={{ xs: 12, md: 3}}>
                <TextField
                  select
                  fullWidth
                  label="Product"
                  value={filters.productId}
                  onChange={(e) => handleFilterChange('productId', e.target.value)}
                >
                  <MenuItem value="">All Products</MenuItem>
                  {products.map((product) => (
                    <MenuItem key={product.productId} value={product.productId}>
                      {product.description}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          )}
          
          {/* Active filters display */}
          {Object.values(filters).some(Boolean) && (
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {filters.status && (
                <Chip 
                  label={`Status: ${filters.status === 'expiring' ? 'Expiring Soon' : filters.status}`}
                  onDelete={() => handleFilterChange('status', '')}
                  size="small"
                />
              )}
              
              {filters.customerId && (
                <Chip 
                  label={`Customer: ${customers.find(c => c.customerId === filters.customerId)?.name || 'Unknown'}`}
                  onDelete={() => handleFilterChange('customerId', '')}
                  size="small"
                />
              )}
              
              {filters.productId && (
                <Chip 
                  label={`Product: ${products.find(p => p.productId === filters.productId)?.description || 'Unknown'}`}
                  onDelete={() => handleFilterChange('productId', '')}
                  size="small"
                />
              )}
              
              {filters.search && (
                <Chip 
                  label={`Search: ${filters.search}`}
                  onDelete={() => handleFilterChange('search', '')}
                  size="small"
                />
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <DataTable
        columns={columns}
        data={filteredSubscriptions}
        loading={loading}
        getRowId={(row) => row.subscriptionId}
        onRowClick={(row) => navigate(`/subscriptions/${row.subscriptionId}`)}
        actions={[
          {
            icon: <ViewIcon />,
            label: 'View Details',
            onClick: (row) => navigate(`/subscriptions/${row.subscriptionId}`),
          },
        ]}
        emptyMessage={
          Object.values(filters).some(Boolean)
            ? "No subscriptions match the current filters."
            : "No subscriptions found."
        }
      />

      {/* Success notification */}
      <Snackbar 
        open={location.state?.success} 
        autoHideDuration={6000} 
        onClose={() => navigate(location.pathname, { replace: true, state: {} })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => navigate(location.pathname, { replace: true, state: {} })}
          severity="success" 
          sx={{ width: '100%' }}
        >
          {location.state?.message || 'Operation completed successfully'}
        </Alert>
      </Snackbar>
    </>
  );
}